import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { lovable } from "@/integrations/lovable";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth-context";
import { track } from "@/lib/analytics";
import { Eye, EyeOff } from "lucide-react";
import { DemoEntry } from "@/components/demo-entry";

export const Route = createFileRoute("/auth")({
  ssr: false,
  head: () => ({ meta: [
    { title: "Sign in — Wealthy Creatives Boardroom" },
    { name: "description", content: "Sign in to your Boardroom or recover access to your account." },
    { property: "og:title", content: "Sign in — Wealthy Creatives Boardroom" },
    { property: "og:description", content: "Access your creative business dashboard and account recovery." },
    { property: "og:type", content: "website" },
    { name: "twitter:card", content: "summary" },
  ] }),
  component: AuthPage,
});

function AuthPage() {
  const { session, loading } = useAuth();
  // navigation happens via a hard reload so state re-initializes cleanly
  const [mode, setMode] = useState<"sign_in" | "sign_up" | "reset">("sign_in");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loginError, setLoginError] = useState("");
  const [resetSent, setResetSent] = useState(false);

  useEffect(() => {
    // If a valid session already exists on mount, hard-navigate to the
    // dashboard so the _authenticated layout re-mounts fresh.
    if (!loading && session) window.location.replace("/dashboard");
  }, [session, loading]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (mode === "reset") return handleForgot();
    setLoginError("");
    setBusy(true);
    try {
      if (mode === "sign_in") {
        const { error } = await supabase.auth.signInWithPassword({ email: email.trim(), password });
        if (error) throw error;
        await track("login", { method: "password" });
        // Hard reload so the whole app (auth context, access hook, router)
        // re-initializes with the new session and lands directly on the
        // dashboard — no bounce through the marketing homepage.
        window.location.replace("/dashboard");
        return;
      } else {
        const { data, error } = await supabase.auth.signUp({
          email: email.trim(),
          password,
          options: {
            emailRedirectTo: window.location.origin,
            data: { full_name: name },
          },
        });
        if (error) throw error;
        await track("signup", { method: "password" });
        if (data.session) {
          // Auto sign-in (email confirmation disabled) — go straight in.
          window.location.replace("/dashboard");
          return;
        }
        toast.success("Account created. Check your inbox to confirm.");
      }
    } catch (err: any) {
      if (mode === "sign_in") {
        setLoginError(err?.code === "invalid_credentials"
          ? "That email and password don't match. Try again, reset your password, or use Google if that's how you joined."
          : err?.message ?? "Unable to sign in. Please try again.");
      } else toast.error(err?.message ?? "Something went wrong");
    } finally {
      setBusy(false);
    }
  }

  async function handleForgot() {
    if (!email.trim()) {
      toast.error("Enter your email above first, then tap Forgot password.");
      return;
    }
    setBusy(true);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email.trim(), {
        redirectTo: `${window.location.origin}/reset-password`,
      });
      if (error) throw error;
      setResetSent(true);
    } catch (err: any) {
      toast.error(err?.message ?? "Could not send reset email");
    } finally {
      setBusy(false);
    }
  }

  async function handleGoogle() {
    setBusy(true);
    try {
      await track("login_start", { method: "google" });
      const res = await lovable.auth.signInWithOAuth("google", {
        redirect_uri: window.location.origin,
      });
      if (res.error) toast.error(res.error.message ?? "Google sign-in failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-10">
          <div className="font-display text-2xl font-semibold tracking-tight text-foreground">
            <span className="text-[color:var(--gold)]">Wealthy Creatives</span> Boardroom
          </div>
          <p className="text-sm text-muted-foreground mt-2">Your CEO command center.</p>
        </div>

        <div className="boardroom-card p-8">
          <div className="flex gap-1 mb-6 p-1 rounded-md bg-surface-2/60">
            {(["sign_in", "sign_up"] as const).map((m) => (
              <Button
                variant="ghost"
                key={m}
                onClick={() => { setMode(m); setLoginError(""); setResetSent(false); }}
                className={`flex-1 text-sm py-2 rounded transition ${
                  mode === m ? "bg-[color:var(--surface-1)] text-foreground" : "text-muted-foreground"
                }`}
              >
                {m === "sign_in" ? "Sign in" : "Create account"}
              </Button>
            ))}
          </div>

          {mode === "reset" && <div className="mb-5">
            <h1 className="font-display text-xl font-semibold">Reset your password</h1>
            <p className="mt-2 text-sm text-muted-foreground">Enter the email you used to join Boardroom.</p>
          </div>}
          {resetSent && <p role="status" className="mb-5 text-sm text-success">If an account uses that email, a reset link is on its way. Check your inbox and spam folder.</p>}
          <form onSubmit={handleSubmit} className="space-y-4">
            {mode === "sign_up" && (
              <div>
                <Label htmlFor="name">Name</Label>
                <Input id="name" value={name} onChange={(e) => setName(e.target.value)} required className="mt-1.5" />
              </div>
            )}
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="mt-1.5" />
            </div>
            {mode !== "reset" && <div>
              <Label htmlFor="password">Password</Label>
              <div className="relative mt-1.5">
                <Input id="password" type={showPassword ? "text" : "password"} autoComplete={mode === "sign_up" ? "new-password" : "current-password"} value={password} onChange={(e) => setPassword(e.target.value)} required minLength={mode === "sign_up" ? 6 : undefined} className="pr-12" />
                <Button type="button" variant="ghost" size="icon" className="absolute right-0 top-0" title={showPassword ? "Hide password" : "Show password"} aria-label={showPassword ? "Hide password" : "Show password"} onClick={() => setShowPassword(!showPassword)}>{showPassword ? <EyeOff /> : <Eye />}</Button>
              </div>
              {mode === "sign_in" && (
                <Button
                  variant="link"
                  type="button"
                  onClick={() => { setMode("reset"); setLoginError(""); setResetSent(false); }}
                  disabled={busy}
                  className="mt-2 h-auto px-0 text-sm text-gold"
                >
                  Forgot password?
                </Button>
              )}
            </div>}
            {loginError && <p role="alert" className="text-sm text-destructive">{loginError}</p>}
            <Button type="submit" disabled={busy} className="w-full bg-[color:var(--gold)] hover:opacity-90 text-[color:var(--primary-foreground)]">
              {busy ? "Please wait…" : mode === "reset" ? "Send reset link" : mode === "sign_in" ? "Sign in" : "Create account"}
            </Button>
          </form>

          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-[color:var(--hairline)]" /></div>
            <div className="relative flex justify-center text-xs"><span className="bg-[color:var(--surface-1)] px-2 text-muted-foreground">or</span></div>
          </div>

          <Button type="button" variant="outline" onClick={handleGoogle} disabled={busy} className="w-full border-[color:var(--hairline)] bg-transparent hover:bg-[color:var(--surface-2)]">
            Continue with Google
          </Button>

          {mode === "sign_in" && <DemoEntry className="mt-4 w-full border-border text-gold" />}

        </div>
      </div>
    </div>
  );
}
